import React from "react";
import { Button, Input, Space } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import TtsAudioControls from "../TtsAudioControls";
import OptionRichText from "../OptionRichText";
import { generateOptionId } from "../gameTypes";

const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

type DragAndDropHorizontalGame = Extract<
  Game,
  { type: "drag_and_drop_horizontal" }
>;

type Props = {
  game: DragAndDropHorizontalGame;
  onChange: (game: DragAndDropHorizontalGame) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const DragAndDropHorizontalEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
}) => {
  // Defensive: partial/legacy games may arrive without an items array.
  // Fall back to an empty list so the map() below doesn't crash.
  const gameItems: DragAndDropHorizontalGame["items"] = Array.isArray(
    game.items,
  )
    ? game.items
    : [];

  const updateItem = (
    index: number,
    patch: Partial<DragAndDropHorizontalGame["items"][number]>,
  ) => {
    const items = gameItems.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    onChange({ ...game, items });
  };

  const addItem = () => {
    onChange({
      ...game,
      items: [...gameItems, { id: generateOptionId(), text: "" }],
    });
  };

  const removeItem = (index: number) => {
    const items = gameItems.filter((_, i) => i !== index);
    onChange({ ...game, items });
  };

  const moveItem = (index: number, dir: -1 | 1) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= gameItems.length) return;
    const items = [...gameItems];
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    onChange({ ...game, items });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>
      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>
      <div>
        <label>
          Elementos (cargalos en el orden correcto, de izquierda a derecha)
        </label>
        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
          La etiqueta de posición (opcional) es el texto fijo que verá el
          alumno sobre cada casilla de la línea de tiempo (ej. el año del
          evento). No se puede arrastrar: marca el lugar correcto.
        </div>
        {gameItems.map((item, idx) => (
          <div
            key={item.id}
            style={{
              marginTop: 8,
              padding: 8,
              border: "1px solid var(--bo-card-border)",
              borderRadius: 6,
            }}
          >
            <div
              style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
            >
              <span style={{ width: 24, marginTop: 8 }}>{idx + 1}.</span>
              <OptionRichText
                value={item.text}
                onChange={(html) => updateItem(idx, { text: html })}
                placeholder="Texto del elemento"
              />
              <TtsAudioControls
                text={stripHtml(item.text)}
                audioUrl={item.audio}
                onAudioChange={(url) => updateItem(idx, { audio: url })}
                generateAudio={generateAudio}
              />
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={() => moveItem(idx, -1)}
                disabled={idx === 0}
              />
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={() => moveItem(idx, 1)}
                disabled={idx === gameItems.length - 1}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeItem(idx)}
                disabled={gameItems.length <= 2}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <Input
                value={item.slotLabel}
                onChange={(e) =>
                  updateItem(idx, { slotLabel: e.target.value })
                }
                addonBefore="Etiqueta de posición"
                placeholder="Ej. 1492 (opcional)"
                style={{ maxWidth: 360 }}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <ImageUploadInput
                value={item.image}
                onChange={(url) => updateItem(idx, { image: url })}
                label="Imagen del elemento"
              />
            </div>
          </div>
        ))}
        <div style={{ marginTop: 8 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addItem}
          >
            Agregar elemento
          </Button>
        </div>
      </div>
    </Space>
  );
};

export default DragAndDropHorizontalEditor;
